import React from 'react';
import styled from 'styled-components';
import type { ThemedStyledProps } from 'styled-components';
import { TextCss, Colors, Fonts } from 'styles';
import spacings from 'styles/spacings';
import type { TextVariant } from 'styles/text';

const COLOR_MAP = {
  primary: 'gray10',
  secondary: 'gray9',
  'secondary-alt': 'gray8',
  success: 'green5',
  error: 'red5',
  black: 'gray1',
  link: 'blue5',
} as const;

type TextOverflow = 'clip' | 'ellipsis' | 'show';

type TextOwnProps = Readonly<{
  variant?: TextVariant;
  display?: 'none' | 'inline' | 'block' | 'inline-block' | 'inline-flex';
  color?: string;
  italic?: boolean;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  gutterBottom?: keyof typeof spacings;
  gutterTop?: keyof typeof spacings;
  children?: React.ReactNode;
  overflow?: TextOverflow;
}>;

export type TextProps = TextOwnProps &
  Omit<React.ComponentPropsWithoutRef<'span'>, keyof TextOwnProps>;

const TEXT_ALIGN = (props: TextProps) => props.align ?? 'inherit';
const DISPLAY = (props: TextProps): string => props.display ?? 'inline';
const GUTTER_BOTTOM = ({ gutterBottom }: ThemedStyledProps<TextProps, never>) =>
  `${spacings[gutterBottom ?? 'none']}rem`;
const GUTTER_TOP = (props: ThemedStyledProps<TextProps, never>) =>
  `${spacings[props.gutterTop ?? 'none']}rem`;
const COLOR = (props: ThemedStyledProps<TextProps, never>): string => {
  const color = props.color ?? 'primary';
  const mappedColor = COLOR_MAP[color as keyof typeof COLOR_MAP] ?? color;
  return Colors[mappedColor as keyof typeof Colors];
};
const FONT_STYLE = (props: TextProps) => (props.italic ? 'font-style: italic;' : '');

const Text = styled.span.withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    defaultValidatorFn(prop) || ['color', 'stretchY'].includes(prop),
})<TextProps>`
  display: ${DISPLAY};
  text-align: ${TEXT_ALIGN};
  margin-bottom: ${(props) => GUTTER_BOTTOM(props as ThemedStyledProps<TextProps, never>)};
  margin-top: ${(props) => GUTTER_TOP(props as ThemedStyledProps<TextProps, never>)};
  color: ${(props) => COLOR(props as ThemedStyledProps<TextProps, never>)};
  font-family: ${Fonts.mainFont};
  ${FONT_STYLE};
  ${(props) => TextCss[(props as TextProps).variant as keyof typeof TextCss] ?? TextCss['body1']};
  ${(props) =>
    (props as TextProps).overflow === 'ellipsis'
      ? 'text-overflow: ellipsis; white-space: nowrap; overflow: hidden;'
      : ''};
  ${(props) => ((props as TextProps).overflow === 'clip' ? 'overflow: hidden;' : '')};
  ${(props) => (props.overflow === 'show' ? 'overflow: visible;' : '')};
`;

export default Text;
